import { Injectable } from '@angular/core';

/**
 * Registry of in-progress operations that must NEVER be interrupted by an
 * automatic reload — Fireblocks co-signing, an on-chain borrow/loan transaction
 * that has already emitted its first tx, and anything else with money in flight.
 *
 * This exists because the flows that care already guard themselves with a
 * `beforeunload` listener keyed on a METHOD-LOCAL closure variable
 * (`isFirstTransactionEmitted`), which no service can read. Registering here
 * makes that same state observable to ReloadSafetyService.
 *
 * Token-based rather than a bare counter on purpose: an unbalanced `end()` in an
 * error path must not drive the count negative and silently unlock the gate.
 */
@Injectable({ providedIn: 'root' })
export class CriticalFlowService {
  private nextToken = 1;
  private readonly active = new Map<number, string>();

  /** Mark the start of an uninterruptible flow. Keep the returned token for end(). */
  begin(label: string): number {
    const token = this.nextToken++;
    this.active.set(token, label);
    return token;
  }

  /** Release a flow. Safe to call twice, or with a token that was never issued. */
  end(token: number): void {
    this.active.delete(token);
  }

  get isActive(): boolean {
    return this.active.size > 0;
  }

  /** Labels of everything currently holding the gate — for logging only. */
  get activeLabels(): string[] {
    return Array.from(this.active.values());
  }
}
